import { CIS_COUNTRIES, PHONE_COUNTRY_CODES } from '@shared-core/features/config/configs/countries'
import { useI18N } from '@shared-core/hooks/use-i18n'
import countriesLocales from '@shared-core/locales/countries.dynamic.i18n.json'
import type { Country } from '@shared-core/utils/countries-meta'
import type { CountryCode } from '@shared-core/utils/phone'
import { flags } from '@shared-images/flags'
import { AppIcon } from '@shared-ui/components/ui/app-icon'
import { GText } from '@shared-ui/components/ui/text'
import { useMemo } from 'react'
import { Image, Modal, Pressable, ScrollView, View } from 'react-native'
import { useCreateStyles } from './styles'

export type AppPhoneCountryPickerProps = {
  isVisible: boolean
  selectedCode: CountryCode
  allowedCodes?: CountryCode[]
  onSelect: (country: Country) => void
  onClose: () => void
  testID?: string
}

export function AppPhoneCountryPicker({
  isVisible,
  selectedCode,
  allowedCodes,
  onSelect,
  onClose,
  testID,
}: AppPhoneCountryPickerProps) {
  const styles = useCreateStyles()
  const { t } = useI18N(countriesLocales)
  const countries = useMemo(
    () =>
      PHONE_COUNTRY_CODES.flatMap((code) =>
        CIS_COUNTRIES.filter(
          (country) => country.code === code && (!allowedCodes?.length || allowedCodes.includes(code)),
        ),
      ),
    [allowedCodes],
  )
  return (
    <Modal visible={isVisible} transparent animationType="slide" onRequestClose={onClose} statusBarTranslucent>
      <View style={styles.overlay}>
        <Pressable style={styles.backdrop} onPress={onClose} />
        <ScrollView style={styles.sheet} contentContainerStyle={styles.content} testID={testID}>
          {countries.map((country) => (
            <Pressable
              key={country.code}
              accessibilityRole="radio"
              accessibilityLabel={`${t(`country-${country.code}` as 'country-ru')} ${country.dialCode}`}
              accessibilityState={{ selected: country.code === selectedCode }}
              style={styles.row}
              onPress={() => {
                onSelect(country)
                onClose()
              }}
            >
              <Image source={flags[country.flagKey]} style={styles.flag} />
              <GText style={styles.name}>{t(`country-${country.code}` as 'country-ru')}</GText>
              <GText>{country.dialCode}</GText>
              {country.code === selectedCode && <AppIcon icon="check" size={20} />}
            </Pressable>
          ))}
        </ScrollView>
      </View>
    </Modal>
  )
}
